import AppComponent from '../../../base/app-component.ts';
import type { UploadUiSpec } from '@/types/casse/upload-ui-spec.ts';
import {
  type CassaUploadState,
  accessoryKeyToRuolo,
  bindAccessorioEvents,
  buildUploadUrl,
  initAccessoriState,
  renderDropzone,
} from './bergamo-upload-shared.ts';

/**
 * wc-bergamo-ce-upload — UI upload per Bergamo Cassa Edile (CE).
 *
 * Pattern: 1 dropzone .rar (archivio cassa edile, FILCA incluso)
 *          + N dropzone accessori sindacali dichiarati da `accessoryInputs`.
 *
 * Riceve la `UploadUiSpec` via `data-options` JSON (vedi wc-cassa-upload).
 * Emette `cassa-upload-changed` ad ogni cambio stato (archivio/accessori).
 */
export class BergamoCeUpload extends AppComponent {
  static override componentName = 'wc-bergamo-ce-upload';
  protected override get templateId() { return 'bergamo-ce-upload'; }

  private state: CassaUploadState = {
    archivio: { ok: false, filename: '' },
    accessori: {},
  };

  protected override afterRender(): void {
    const spec = this.readSpec();
    if (!spec) return;

    this.state.accessori = initAccessoriState(spec.accessoryInputs);

    this.mountArchivioDropzone(spec);
    this.mountAccessoriDropzones(spec);
  }

  private readSpec(): UploadUiSpec | null {
    const raw = this.getAttribute('data-options');
    if (!raw) {
      console.warn('[wc-bergamo-ce-upload] data-options assente');
      return null;
    }
    try {
      return JSON.parse(raw) as UploadUiSpec;
    } catch {
      console.warn('[wc-bergamo-ce-upload] data-options non e\' JSON valido');
      return null;
    }
  }

  private mountArchivioDropzone(spec: UploadUiSpec): void {
    const container = this.$('[data-dropzone-container="archivio"]');
    if (!container) return;

    const codice = this.attributeString('codice', 'CE');
    const anno = this.attributeString('anno');
    const mese = this.attributeString('mese');
    const url = buildUploadUrl(codice, anno, mese, 'archivio');

    const el = renderDropzone(container, {
      url,
      accept: spec.acceptTypes.join(','),
      maxFiles: 1,
      send: 'auto',
      showUploadBtn: true,
    });
    if (!el) return;

    el.addEventListener('uploaded.wx.dropzone', (e: Event) => {
      const detail = (e as CustomEvent).detail as { response?: { filename?: string } } | undefined;
      this.state.archivio = {
        ok: true,
        filename: detail?.response?.filename ?? '',
      };
      this.emitChange();
    });

    el.addEventListener('error.wx.dropzone', () => {
      this.state.archivio = { ok: false, filename: '' };
      this.emitChange();
    });
  }

  private mountAccessoriDropzones(spec: UploadUiSpec): void {
    if (!spec.accessoryInputs) return;
    const codice = this.attributeString('codice', 'CE');
    const anno = this.attributeString('anno');
    const mese = this.attributeString('mese');

    for (const [key, input] of Object.entries(spec.accessoryInputs)) {
      const container = this.$(`[data-dropzone-container="${key}"]`);
      if (!container) continue;

      const ruolo = accessoryKeyToRuolo(key);
      const url = buildUploadUrl(codice, anno, mese, ruolo);

      const el = renderDropzone(container, {
        url,
        accept: input.acceptTypes.join(','),
        maxFiles: 1,
        send: 'auto',
        showUploadBtn: true,
      });
      if (!el) continue;

      bindAccessorioEvents(el, key, this.state, () => this.emitChange());
    }
  }

  private emitChange(): void {
    this.dispatch('cassa-upload-changed', {
      archivio: this.state.archivio,
      accessori: { ...this.state.accessori },
    });
  }
}
